import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logService } from '../services/log.service';
import { useLang } from '../contexts/LangContext';
import { APP_CONFIG } from '../config/app.config';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';

interface LogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error';
  category: string;
  message: string;
  data?: any;
}

const ViewLogs: React.FC = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState<'all' | 'info' | 'warn' | 'error'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    loadLogs();
    loadStats();
  }, []);

  const loadLogs = async () => {
    try {
      const allLogs = await logService.getLogs();
      setLogs(allLogs.reverse()); // Show newest first
    } catch (error) {
      console.error('Failed to load logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statistics = await logService.getStats();
      setStats(statistics);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleClearLogs = async () => {
    const confirmed = window.confirm('Clear all debug logs? This cannot be undone.');
    if (!confirmed) return;

    try {
      await logService.clearLogs();
      setLogs([]);
      await loadStats();
    } catch (error) {
      console.error('Failed to clear logs:', error);
      alert('Failed to clear logs');
    }
  };

  const handleExportLogs = async () => {
    try {
      const json = await logService.exportLogs();
      const app_name = APP_CONFIG.NAME.replace(/\s+/g, '-').toLowerCase();
      const fileName = `${app_name}-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;  // Generic filename
      
      if (Capacitor.isNativePlatform()) {
        // Native platform (Android/iOS) - use Capacitor
        console.log('[Logs Export] Starting native export...', { fileName, platform: Capacitor.getPlatform() });
        
        try {
          // Write to Cache directory (doesn't require permissions)
          const result = await Filesystem.writeFile({
            path: fileName,
            data: json,
            directory: Directory.Cache,
            encoding: Encoding.UTF8,
          });
          
          console.log('[Logs Export] File written successfully:', result.uri);
          
          // Check if Share is available
          const canShare = await Share.canShare();
          console.log('[Logs Export] Can share:', canShare);
          
          if (!canShare) {
            throw new Error('Share functionality is not available on this device');
          }
          
          // Open share sheet for user to save
          await Share.share({
            title: 'Save Debug Logs',
            text: `${APP_CONFIG.NAME} Debug Logs`,  // Use app name from config
            url: result.uri,
            dialogTitle: 'Save your debug logs file',
          });
          
          console.log('[Logs Export] Share dialog shown successfully');
        } catch (nativeError: any) {
          console.error('[Logs Export] Native export failed:', nativeError);
          alert(`Failed to export logs on native platform: ${nativeError.message}`);
        }
      } else {
        // Web platform - use traditional download
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to export logs:', error);
      alert('Failed to export logs');
    }
  };

  const filteredLogs = logs.filter(log => {
    if (filterLevel !== 'all' && log.level !== filterLevel) return false;
    if (filterCategory !== 'all' && log.category !== filterCategory) return false;
    return true;
  });

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'info': return 'text-blue-600 bg-blue-50';
      case 'warn': return 'text-yellow-600 bg-yellow-50';
      case 'error': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'info':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'warn':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading logs...</div>
      </div>
    );
  }

  const categories = stats?.categories || [];

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="px-scr py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={() => navigate('/settings')} className="mr-2">
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-800">Debug Logs</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportLogs}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
              title="Export Logs"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>
            <button
              onClick={handleClearLogs}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
              title="Clear All Logs"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="px-scr py-4 bg-white border-b">
          <div className="grid grid-cols-4 gap-3 text-center mb-3">
            <div className="bg-sky-100/75 p-2 rounded-lg">
              <div className="text-2xl font-bold text-gray-800">{stats.totalLogs}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="bg-sky-100/75 p-2 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{stats.infoCount}</div>
              <div className="text-xs text-gray-500">Info</div>
            </div>
            <div className="bg-sky-100/75 p-2 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{stats.warnCount}</div>
              <div className="text-xs text-gray-500">Warn</div>
            </div>
            <div className="bg-sky-100/75 p-2 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{stats.errorCount}</div>
              <div className="text-xs text-gray-500">Error</div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 pt-3 border-t">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
            </svg>
            <span className="text-sm text-gray-600">
              Storage: <strong>{stats.sizeKB} KB</strong> / {stats.maxSizeKB} KB
            </span>
            <div className="ml-2 flex-1 max-w-xs">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    (stats.sizeKB / stats.maxSizeKB) > 0.9 ? 'bg-red-600' :
                    (stats.sizeKB / stats.maxSizeKB) > 0.7 ? 'bg-yellow-600' :
                    'bg-green-600'
                  }`}
                  style={{ width: `${Math.min((stats.sizeKB / stats.maxSizeKB) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="px-scr py-4 bg-white border-b space-y-3">
        <div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setFilterLevel('all')}
              className={`grow px-4 py-2 text-sm font-semibold rounded-full ${
                filterLevel === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterLevel('info')}
              className={`grow px-4 py-2 text-sm font-semibold rounded-full ${
                filterLevel === 'info' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700'
              }`}
            >
              Info
            </button>
            <button
              onClick={() => setFilterLevel('warn')}
              className={`grow px-4 py-2 text-sm font-semibold rounded-full ${
                filterLevel === 'warn' ? 'bg-yellow-500 text-white' : 'bg-yellow-100 text-yellow-700'
              }`}
            >
              Warn
            </button>
            <button
              onClick={() => setFilterLevel('error')}
              className={`grow px-4 py-2 text-sm font-semibold rounded-full ${
                filterLevel === 'error' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700'
              }`}
            >
              Error
            </button>
          </div>
        </div>

        <div>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="input-base select-base select-sm !text-base w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Categories</option>
            {categories.map((cat: string) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Logs List */}
      <div className="px-scr py-4 space-y-2">
        {filteredLogs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No logs to display</p>
            <p className="text-sm mt-2">Enable Developer Mode in Settings to start capturing logs</p>
          </div>
        ) : (
          filteredLogs.map((log, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-sm overflow-hidden"
            >
              <div
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                className="px-4 py-3 cursor-pointer hover:bg-gray-50"
              >
                <div className="flex items-start gap-3">
                  <div className={`p-1 rounded ${getLevelColor(log.level)}`}>
                    {getLevelIcon(log.level)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-gray-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700">
                        {log.category}
                      </span>
                    </div>
                    <p className="text-sm text-gray-800">{log.message}</p>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      expandedIndex === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {expandedIndex === index && log.data && (
                <div className="px-4 py-3 bg-gray-50 border-t">
                  <pre className="text-xs font-mono text-gray-700 overflow-x-auto whitespace-pre-wrap break-words">
                    {JSON.stringify(log.data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ViewLogs;
